import * as Msal from 'msal'
import router from '../router'

export interface AuthenticationServiceConfig {
  auth: AuthConfig;
  cache: CacheConfig;
  scopes: string[];
}

export interface CacheConfig {
  cacheLocation: Msal.CacheLocation;
  storeAuthStateInCookie: boolean;
}

export interface AuthConfig {
  clientId: string;
  authority: string;
  validateAuthority: false;
  redirectUri: string;
  navigateToLoginRequestUrl: boolean;
  postLogoutRedirectUri: string | (() => string);
}

export interface ResponseConfig {
  scopes: Array<string>;
  extraQueryParameters: { [parameter: string]: string };
}

export enum LoginType {
  Request = 1,
  Response = 2,
  ResponseReview = 3
}

export class AuthService {
  app: Msal.UserAgentApplication

  loginScopes = ['openid']

  logoutUrl = ''

  public initialize(
    authenticationConfig: AuthenticationServiceConfig,
  ) {
    this.loginScopes = authenticationConfig.scopes

    if (typeof authenticationConfig.auth.postLogoutRedirectUri === 'string') {
      this.logoutUrl = authenticationConfig.auth.postLogoutRedirectUri
      // eslint-disable-next-line no-param-reassign
      authenticationConfig.auth.postLogoutRedirectUri = () => this.logoutUrl
    }

    this.app = new Msal.UserAgentApplication(authenticationConfig)
    this.app.handleRedirectCallback((error, response) => {
      if (response?.accountState) {
        const route = JSON.parse(response.accountState)
        router.push(route)
      }
    })
  }

  isCallback() {
    return this.app?.isCallback(window.location.hash)
  }

  getAccount() {
    return this.app?.getAccount() || null
  }

  public async acquireToken(): Promise<string | undefined> {
    const request = {
      scopes: this.loginScopes,
    }
    try {
      const response = await this.app.acquireTokenSilent(request)
      return response.accessToken
    } catch (err) {
      if ((err as Error).name === 'InteractionRequiredAuthError') {
        try {
          await this.app.acquireTokenPopup(request)
        // eslint-disable-next-line no-empty
        } catch (e) { }
      }
    }

    return undefined
  }

  public login(params: {
    lang: string;
    projectId: string;
    procedureId: string;
    noticeId: string;
    enoticeId: string;
    documentId: string;
    loginType: LoginType;
    redirectRouteName: string;
    msp: string | (string | null)[];
  }) {
    const redirectRoute = {
      name: params.redirectRouteName,
      params: {
        lang: params.lang,
        documentId: params.documentId,
        msp: params.msp,
      },
    }

    const request = {
      scopes: this.loginScopes,
      extraQueryParameters: {
        ui_locales: params.lang,
        espdRequestedProject: params.loginType !== LoginType.Response ? params.projectId : '0',
        espdRequestedProcedure: params.loginType !== LoginType.Response ? params.procedureId : '0',
        msp: params.msp.toString(),
      },
      state: JSON.stringify(redirectRoute),
      forceRefresh: true,
    }

    switch (this.app.getCurrentConfiguration().cache?.cacheLocation) {
      case 'sessionStorage':
        sessionStorage.clear()
        break
      case 'localStorage':
        localStorage.clear()
        break
      default:
        break
    }

    localStorage.setItem('login-locale', request.extraQueryParameters.ui_locales)
    localStorage.setItem('msp', request.extraQueryParameters.msp)

    this.app.loginRedirect(request)
  }

  public logout(redirect: string | undefined = undefined) {
    if (redirect) {
      this.logoutUrl = redirect
    }
    this.app.logout()
  }
}

export const instance: AuthService = new AuthService()

export function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}
